import React, { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";
import {
  holidaysDelete,
  holidaysList,
  holidaysStatus,
  holidaysSendMessage,
} from "../../../api/Holidays";
import { formatToDate } from "../../master/FormatToDateTime";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import usePrivateLink from "../../config/usePrivateLink";

export default function Holidays() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const hasAccess = usePrivateLink();

  const getHolidaysList = async () => {
    setLoading(true);

    try {
      const response = await holidaysList();

      if (response?.data?.status === true) {
        setData(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load holidays.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ??
          err?.message ??
          "Unable to load holidays.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHolidaysList();
  }, []);

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const response = await holidaysDelete(id);

      if (response?.data?.status === true) {
        SweetAlertShow("success", "Successfully", response?.data?.message);

        await getHolidaysList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);

    try {
      const response = await holidaysStatus({
        id,
        status,
      });

      if (response?.data?.status === true) {
        SweetAlertShow("success", "Successfully", response?.data?.message);

        await getHolidaysList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    Swal.fire({
      title: "Delete Holiday?",
      text: "This holiday will be moved to deleted status.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(id);
      }
    });
  };

  const statusInfo = (status) => {
    switch (String(status)) {
      case "1":
        return {
          text: "Approved",
          className: "bg-label-success",
          icon: "ri-checkbox-circle-line",
        };

      case "2":
        return {
          text: "Cancelled",
          className: "bg-label-danger",
          icon: "ri-close-circle-line",
        };

      default:
        return {
          text: "Pending",
          className: "bg-label-warning",
          icon: "ri-time-line",
        };
    }
  };

  const handleSendMessage = async (id) => {
    const result = await Swal.fire({
      title: "Send Holiday Message?",
      text: "Holiday announcement will be sent to the applicable users.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Message",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const response = await holidaysSendMessage(id);

      if (response?.data?.status === true) {
        SweetAlertShow(
          "success",
          "Message Queued",
          response?.data?.message ||
            "Holiday message has been queued successfully.",
        );

        await getHolidaysList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ??
          err?.message ??
          "Unable to send holiday message.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    <div className="card">
                      {/* HEADER */}
                      <div className="card-header">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <h4 className="text-primary mb-1">
                              <i className="ri-calendar-event-line me-2" />
                              Holidays
                            </h4>

                            <small className="text-muted">
                              Manage institute holidays and leave days
                            </small>
                          </div>

                          {hasAccess("/holidays/add") && (
                            <Link
                              to="/holidays/add"
                              className="btn btn-primary"
                            >
                              <i className="ri-add-line me-1" />
                              Add Holiday
                            </Link>
                          )}
                        </div>
                      </div>

                      <div className="card-body">
                        {/* SUMMARY */}
                        <div className="row g-3 mb-4">
                          <div className="col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-md me-3">
                                  <span className="avatar-initial rounded bg-label-primary">
                                    <i className="ri-calendar-line" />
                                  </span>
                                </div>

                                <div>
                                  <small className="text-muted">
                                    Total Holidays
                                  </small>

                                  <h4 className="mb-0">{data.length}</h4>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-md me-3">
                                  <span className="avatar-initial rounded bg-label-success">
                                    <i className="ri-checkbox-circle-line" />
                                  </span>
                                </div>

                                <div>
                                  <small className="text-muted">Approved</small>

                                  <h4 className="mb-0">
                                    {
                                      data.filter(
                                        (x) => String(x.status) === "1",
                                      ).length
                                    }
                                  </h4>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-md me-3">
                                  <span className="avatar-initial rounded bg-label-warning">
                                    <i className="ri-time-line" />
                                  </span>
                                </div>

                                <div>
                                  <small className="text-muted">Pending</small>

                                  <h4 className="mb-0">
                                    {
                                      data.filter(
                                        (x) => String(x.status) === "0",
                                      ).length
                                    }
                                  </h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* EMPTY STATE */}
                        {!loading && data.length === 0 ? (
                          <div className="text-center py-5">
                            <div
                              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-label-primary"
                              style={{
                                width: 80,
                                height: 80,
                              }}
                            >
                              <i
                                className="ri-calendar-close-line text-primary"
                                style={{ fontSize: 40 }}
                              />
                            </div>

                            <h5>No Holidays Found</h5>

                            <p className="text-muted mb-3">
                              No holiday records are available.
                            </p>

                            {hasAccess("/holidays/add") && (
                              <Link
                                to="/holidays/add"
                                className="btn btn-primary"
                              >
                                <i className="ri-add-line me-1" />
                                Add First Holiday
                              </Link>
                            )}
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover align-middle">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Holiday</th>
                                  <th>Applicable</th>
                                  <th>Date</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                  <th>Message</th>
                                  <th>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {data.map((item, i) => {
                                  const status = statusInfo(item?.status);

                                  return (
                                    <tr key={item?.id || i}>
                                      <td>
                                        <span className="fw-semibold">
                                          {i + 1}
                                        </span>
                                      </td>

                                      {/* TITLE + DESCRIPTION */}
                                      <td>
                                        <div className="d-flex align-items-start">
                                          <div className="avatar avatar-sm me-2">
                                            <span className="avatar-initial rounded bg-label-primary">
                                              <i className="ri-calendar-event-line" />
                                            </span>
                                          </div>

                                          <div>
                                            <div className="fw-semibold">
                                              {item?.title || "-"}
                                            </div>

                                            {item?.description && (
                                              <small className="text-muted d-block">
                                                {item.description}
                                              </small>
                                            )}
                                          </div>
                                        </div>
                                      </td>

                                      {/* APPLICABLE */}
                                      <td>
                                        <span className="badge bg-label-info">
                                          <i className="ri-group-line me-1" />

                                          {item?.applicable === "all"
                                            ? "All"
                                            : item?.applicable === "batch"
                                              ? "Batch"
                                              : "Staff"}
                                        </span>

                                        {/* BATCHES */}
                                        {item?.applicable === "batch" &&
                                          item?.batches?.length > 0 && (
                                            <div className="mt-1">
                                              {item.batches.map((batch) => (
                                                <span
                                                  key={batch.id}
                                                  className="badge bg-label-secondary me-1 mb-1"
                                                >
                                                  {batch.title}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                      </td>

                                      {/* DATE */}
                                      <td>
                                        <div className="fw-semibold">
                                          <i className="ri-calendar-2-line me-1 text-primary" />
                                          {formatToDate(item?.date)}
                                        </div>
                                      </td>

                                      {/* TYPE */}
                                      <td>
                                        <span
                                          className={`badge ${
                                            item?.leave_day_type === "full"
                                              ? "bg-label-primary"
                                              : "bg-label-warning"
                                          }`}
                                        >
                                          <i
                                            className={
                                              item?.leave_day_type === "full"
                                                ? "ri-sun-line me-1"
                                                : "ri-time-line me-1"
                                            }
                                          />

                                          {item?.leave_day_type === "full"
                                            ? "Full Day"
                                            : "Half Day"}
                                        </span>
                                      </td>

                                      {/* STATUS */}
                                      <td>
                                        {hasAccess(
                                          "/holidays/status-change",
                                        ) ? (
                                          <select
                                            className="form-select form-select-sm"
                                            value={item?.status ?? "0"}
                                            onChange={(e) =>
                                              handleStatusChange(
                                                item?.id,
                                                e.target.value,
                                              )
                                            }
                                            style={{
                                              minWidth: 125,
                                            }}
                                          >
                                            <option value="0">Pending</option>
                                            <option value="1">Approved</option>
                                            <option value="2">Cancelled</option>
                                          </select>
                                        ) : (
                                          <span
                                            className={`badge ${status.className}`}
                                          >
                                            <i
                                              className={`${status.icon} me-1`}
                                            />
                                            {status.text}
                                          </span>
                                        )}
                                      </td>

                                      {/* MESSAGE STATUS */}
                                      <td>
                                        {String(item?.message_status) ===
                                        "1" ? (
                                          <span className="badge bg-label-success">
                                            <i className="ri-send-plane-line me-1" />
                                            Sent
                                          </span>
                                        ) : (
                                          <span className="badge bg-label-secondary">
                                            <i className="ri-mail-close-line me-1" />
                                            Not Sent
                                          </span>
                                        )}
                                      </td>

                                      {/* ACTION */}
                                      <td>
                                        <div className="d-flex">
                                          {hasAccess("/holidays/update") && (
                                            <Link
                                              to={`/holidays/update?id=${item?.id}`}
                                              className="btn btn-sm btn-label-info me-1"
                                              title="Edit"
                                            >
                                              <i className="ri-edit-circle-line" />
                                            </Link>
                                          )}

                                          {hasAccess("/holidays/delete") && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-label-danger me-1"
                                              title="Delete"
                                              onClick={() =>
                                                confirmDelete(item?.id)
                                              }
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </button>
                                          )}

                                          {hasAccess(
                                            "/holidays/send-message",
                                          ) && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-label-success me-1"
                                              title={
                                                String(item?.message_status) ===
                                                "1"
                                                  ? "Message Sent"
                                                  : "Send Message"
                                              }
                                              disabled={
                                                String(item?.message_status) ===
                                                "1"
                                              }
                                              onClick={() =>
                                                handleSendMessage(item?.id)
                                              }
                                            >
                                              <i
                                                className={
                                                  String(
                                                    item?.message_status,
                                                  ) === "1"
                                                    ? "ri-checkbox-circle-line"
                                                    : "ri-send-plane-line"
                                                }
                                              />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
